import { displayInitialsForStaff } from '@/lib/scheduling';
import type { JobType, Staff } from '@/lib/types';

const ROLE_GROUPS: { jobTypes: JobType[]; label: string; color: { bg: string; text: string } }[] = [
  { jobTypes: ['pharmacist', 'manager'], label: 'Pharmacists', color: { bg: '#E6F1FB', text: '#0C447C' } },
  { jobTypes: ['pharmacy_technician'],   label: 'Technicians', color: { bg: '#FAEEDA', text: '#633806' } },
  { jobTypes: ['pharmacy_assistant'],    label: 'Assistants',  color: { bg: '#EAF3DE', text: '#27500A' } },
  { jobTypes: ['student'],               label: 'Students',    color: { bg: '#EEEDFE', text: '#3C3489' } },
  { jobTypes: ['other'],                 label: 'Other',       color: { bg: '#F1EFE8', text: '#2C2C2A' } },
];

export interface StaffSidebarProps {
  staff: Staff[];
  primaryLocationId: string;
  onAdd?: () => void;
}

export function StaffSidebar({ staff, primaryLocationId, onAdd }: StaffSidebarProps) {
  const today = new Date().toISOString().slice(0, 10);
  const activeStaff = staff.filter((s) => !s.active_to || s.active_to > today);

  const grouped = ROLE_GROUPS.map((group) => ({
    ...group,
    members: activeStaff.filter((s) => group.jobTypes.includes(s.job_type)),
  })).filter((g) => g.members.length > 0);

  return (
    // sticky + self-start + max-h-screen: the schedule page scrolls the
    // whole window (the calendar grid's own overflow-auto never engages
    // because the h-full chain isn't height-constrained by AppShell's
    // <main>). Without sticky, this panel scrolls up out of view on a tall
    // month, so you can't drag staff onto the last weeks' day cells.
    // Pinning it to the viewport top keeps the drag source reachable for
    // every row. The inner list already has overflow-y-auto, so a long
    // roster scrolls inside the capped height instead of overflowing.
    <aside
      className="w-56 shrink-0 border-r border-border bg-muted/30 flex flex-col sticky top-0 self-start max-h-screen print:hidden"
      style={{ minHeight: 600 }}
    >
      <div className="p-3 border-b border-border flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-sm font-medium">Staff</h2>
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted-foreground/20 text-foreground">{activeStaff.length}</span>
        </div>
        {onAdd ? (
          <button type="button" onClick={onAdd} className="text-xs px-2 py-0.5 rounded-md bg-primary text-primary-foreground hover:opacity-90">+ Add</button>
        ) : (
          <a href="/staff/new" className="text-xs px-2 py-0.5 rounded-md bg-primary text-primary-foreground hover:opacity-90">+ Add</a>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-3">
        {grouped.map((group) => (
          <div key={group.label}>
            <div className="flex items-center justify-between px-1 mb-1">
              <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{group.label}</h3>
              <span className="text-[10px] text-muted-foreground">{group.members.length}</span>
            </div>
            <ul className="space-y-1">
              {group.members.map((s) => (
                <li key={s.id}>
                  <div
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'copy';
                      e.dataTransfer.setData('text/x-pharmasync-staff', s.id);
                    }}
                    className="flex items-center gap-2 p-1.5 rounded-md bg-card border border-border hover:border-foreground/30 cursor-grab active:cursor-grabbing"
                    title={`${s.display_name} - drag onto a calendar slot`}
                  >
                    <span
                      className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium"
                      style={{ background: group.color.bg, color: group.color.text }}
                    >
                      {displayInitialsForStaff(s)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate">{s.display_name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {s.employment_type.replace('_', '-')}
                        {s.job_type === 'manager' && ' · Mgr'}
                        {s.primary_location_id && s.primary_location_id !== primaryLocationId && ' · other loc'}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {grouped.length === 0 && (
          <p className="text-xs text-muted-foreground px-2 py-4 text-center">
            No active staff yet. Click <strong>+ Add</strong> to invite someone.
          </p>
        )}
      </div>
    </aside>
  );
}
